import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AgreementSidebar from './Sidebar.tsx';
import Logo from '@/components/common/Logo';
import GuideOverlay from '@/components/common/GuideOverlay';
import { useGuideOverlay } from '@/components/common/GuideOverlay/useGuideOverlay';
import { GuideStep } from '@/components/common/GuideOverlay/types';
import HomeImage from '@/assets/guide/home-img.gif';

import { useLanguage } from '@/contexts/LanguageContext';
import { useCampaignStatus } from '@/contexts/CampaignStatusContext';
import generationService from '@/services/amai/generation.service';
import CampaignBanner from '../MainLayout/CampaignBanner';

/**
 * 协议页面布局组件
 *
 * 特点：
 * 1. 公开访问，无需登录即可查看协议内容
 * 2. 包含侧边导航，但对导航项进行登录状态检查
 * 3. 未登录用户点击需要认证的功能时跳转到登录页
 */
const AgreementLayout: React.FC = () => {
  const { t } = useLanguage();
  const { campaignStatus, setCampaignStatus, getFeatureStatus, bannerClosed } = useCampaignStatus();
  const [guideSteps, setGuideSteps] = useState<GuideStep[]>([]);
  const location = useLocation();

  useEffect(() => {
    const fetchFeatureSwitch = async () => {
      try {
        const response = await generationService.getFeatureSwitch();
        if (response.code === 0 && response.data) {
          setCampaignStatus(response.data);
        }
      } catch (error) {
        console.error('[AgreementLayout] 获取功能开关失败:', error);
      }
    };
    if (!campaignStatus) {
      fetchFeatureSwitch();
    }
  }, [campaignStatus, setCampaignStatus]);

  const showCampaignBanner = location.pathname === '/home' && getFeatureStatus('SD2.5-1080P-72off') === 1 && !bannerClosed;

  const {
    visible: guideVisible,
    currentStep,
    start: startGuide,
    stop: stopGuide,
    next: nextGuide,
    prev: prevGuide,
  } = useGuideOverlay({ steps: guideSteps });

  const closeGuideHandler = () => {
    stopGuide();
    const guideShown = JSON.parse(localStorage.getItem('guide_shown') || '{}');
    const currentStepKey = guideSteps[currentStep]?.key;
    if (currentStepKey) {
      guideShown[currentStepKey] = true;
      localStorage.setItem('guide_shown', JSON.stringify(guideShown));
    }
  };

  // 根据路由设置引导步骤
  useEffect(() => {
    const guideShown = JSON.parse(localStorage.getItem('guide_shown') || '{}');
    if (location.pathname === '/home' && !guideShown.home) {
      setGuideSteps([
        {
          title: t('home.findMoreCreationTools'),
          content: '',
          key: 'home',
          image: HomeImage,
          imageAlt: t('home.findMoreCreationTools'),
          top: 316,
          left: 135,
          indicator: {
            show: true,
            position: 'left',
            text: t('sidebar.tools'),
          },
        },
      ]);
    }
  }, [location.pathname, t]);

  // steps 更新后再启动引导，确保 GuideOverlay 拿到非空 steps
  useEffect(() => {
    if (guideSteps.length > 0) {
      startGuide();
    }
  }, [guideSteps]);

  return (
    <>
      {showCampaignBanner && <CampaignBanner />}
      <div
        style={{
          display: 'flex',
          height: showCampaignBanner ? 'calc(var(--vh, 1vh) * 100 - 40px)' : 'calc(var(--vh, 1vh) * 100)',
          background: '#0A0A0A',
          overflow: 'hidden',
        }}
      >
      {/* 侧边栏 */}
      <div
        style={{
          width: '245px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        {/* Logo区域 */}
        <div
          style={{
            // borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            background: '#101010',
          }}
        >
          <Logo />
        </div>

        {/* 侧边导航 */}
        <AgreementSidebar />
      </div>

      {/* 主内容区域 */}

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 页面内容 */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            background: '#0A0A0A',
          }}
        >
          <Outlet/>
        </div>
      </div>

      {/* 引导组件 */}
      <GuideOverlay
        visible={guideVisible}
        steps={guideSteps}
        currentStep={currentStep}
        onClose={closeGuideHandler}
        onNext={nextGuide}
        onPrev={prevGuide}
        onComplete={closeGuideHandler}
        maskClosable={true}
        showCloseButton={true}
        showNavigation={true}
        showStepIndicator={true}
      />
    </div>
    </>
  );
};

export default AgreementLayout;
